import React from 'react';

import { Button } from '@/components/ui/button';
import { Camera, LogOut } from 'lucide-react';

// Fecha y hora en Bogotá: "26/09/2026" y "09:35".
const fechaBogota = (d) =>
  new Date(d).toLocaleDateString('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: '2-digit', year: 'numeric' });
const horaBogota = (d) =>
  new Date(d).toLocaleTimeString('en-GB', { timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit' });
const diaBogota = (d) => new Date(d).toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const aMin = (hhmm) => {
  const m = String(hhmm || '').match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Momento en que se marcó la salida. Los registros nuevos lo guardan en fechaSalida; en los
 * anteriores quedó en updatedAt (la marcación de salida es lo único que lo cambia), y se usa
 * solo si su hora coincide con la hora de salida.
 */
export const fechaSalidaDe = (row) => {
  if (!row?.horaSalida) return null;
  if (row.fechaSalida) return new Date(row.fechaSalida);
  if (!row.updatedAt) return null;
  const dif = Math.abs((aMin(horaBogota(row.updatedAt)) ?? -999) - (aMin(row.horaSalida) ?? 999));
  return dif <= 2 || dif >= 1438 ? new Date(row.updatedAt) : null;
};

// Más de esto entre la llegada y la salida casi siempre es una salida olvidada.
const MAX_HORAS_TURNO = 16;

/** Días entre la llegada y la salida, horas reales y si conviene revisarlo. */
export const salidaInfo = (row) => {
  const salida = fechaSalidaDe(row);
  if (!salida || !row.fecha) return null;
  const dias = Math.round((new Date(`${diaBogota(salida)}T12:00:00Z`) - new Date(`${diaBogota(row.fecha)}T12:00:00Z`)) / 86400000);
  const horasReales = (salida - new Date(row.fecha)) / 3600000;
  return { salida, dias, horasReales, revisar: horasReales > MAX_HORAS_TURNO || dias > 1 };
};

const FechaHora = ({ fecha, hora }) => (
  <div className="leading-tight">
    <div className="font-medium tabular-nums">{hora || 'N/A'}</div>
    {fecha && <div className="text-xs text-muted-foreground tabular-nums">{fechaBogota(fecha)}</div>}
  </div>
);

export const workedHoursColumns = [
  {
    id: 'empleado.nombre',
    label: 'Nombre del Empleado',
    type: 'select',
    sortable: true,
    required: true,
    isFilter: true,
    render: (row) => row.empleado?.nombre || 'N/A',
  },
  {
    id: 'empleado.cedula',
    label: 'Documento',
    type: 'text',
    sortable: false,
    required: false,
    isFilter: true,
    render: (row) => row.empleado?.cedula || 'N/A',
  },
  {
    id: 'fecha',
    label: 'Fecha',
    type: 'daterange',
    sortable: true,
    required: true,
    isFilter: true,
    render: (row) => new Date(row.fecha).toLocaleDateString(),
  },
  {
    id: 'horaIngreso',
    label: 'Llegada',
    type: 'time',
    sortable: true,
    required: true,
    isFilter: false,
    // Restricción de llegada: marca las llegadas sin planificar (modo aviso) y las excepciones
    render: (row) => {
      const p = row.planificacion;
      const etiqueta = p && !p.planificado
        ? p.excepcion
          ? { texto: 'Excepción', clase: 'bg-red-100 text-red-800', titulo: `Registrada por excepción: ${p.excepcion.motivo}${p.excepcion.autorizadoPor?.nombre ? ` · ${p.excepcion.autorizadoPor.nombre}` : ''}` }
          : p.modo !== 'apagado'
            ? { texto: 'Sin planificar', clase: 'bg-amber-100 text-amber-800', titulo: 'El empleado marcó sin turno planificado en ese momento' }
            : null
        : null;
      return (
        <div className="flex items-center gap-2">
          <FechaHora fecha={row.fecha} hora={row.horaIngreso} />
          {etiqueta && (
            <span className={`${etiqueta.clase} text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap`} title={etiqueta.titulo}>
              {etiqueta.texto}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'horaSalida',
    label: 'Salida',
    type: 'time',
    sortable: true,
    required: true,
    isFilter: false,
    render: (row) => {
      if (!row.horaSalida) return 'N/A';
      const info = salidaInfo(row);
      return (
        <div className="flex items-center gap-2">
          <FechaHora fecha={info?.salida} hora={row.horaSalida} />
          {info?.dias > 0 && (
            <span
              className={`${info.revisar ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'} text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap`}
              title={`Salida marcada ${info.dias} día(s) después de la llegada: ${info.horasReales.toFixed(1)} h entre llegada y salida.${
                info.revisar ? ' Parece una salida olvidada: revisa y autoriza las horas reales.' : ''
              }`}
            >
              {info.revisar ? `Revisar · ${info.horasReales.toFixed(0)} h` : `+${info.dias} día`}
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'totalHorasTrabajadas',
    label: 'Total Horas',
    type: 'number',
    sortable: true,
    required: true,
    isFilter: false,
    render: (row) => {
      const horas = row.totalHorasTrabajadas;

      return (
        <div className="flex items-center gap-2">
          <span>{horas?.toFixed(2) || 'N/A'}</span>
          {row.almuerzo && (
            <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 rounded-full">
              Almuerzo
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: 'totalHorasAutorizadas',
    label: 'Total Horas Autorizadas',
    type: 'number',
    sortable: true,
    required: true,
    isFilter: false,
    render: (row) => row.totalHorasAutorizadas?.toFixed(2) || 'N/A',
  },
  {
    id: 'foto',
    label: 'Evidencia',
    type: 'image',
    sortable: true,
    required: true,
    isFilter: false,
    render: (row, column, props) => {
      const { onImageClick } = props;
      const imageUrl = row.foto;


      if (!imageUrl) {
        return <span className="text-xs text-gray-500">Sin foto</span>;
      }

      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onImageClick && onImageClick(imageUrl)}
        >
          <Camera className="mr-2 h-4 w-4" />
          Ver
        </Button>
      );
    },
  },
  {
    id: 'isAprobado',
    label: 'Estado',
    type: 'boolean',
    sortable: true,
    required: true,
    isFilter: true,
    render: (row) => row.isAprobado ? 'Aprobado' : 'Pendiente',
  },
  {
    id: 'opciones',
    label: 'Opciones',
    width: '10%',
    actions: [
      {
        key: 'edit',
        icon: 'LogOut',
        tooltip: 'Registrar salida',
        className: 'text-green-400 hover:text-green-600',
        showWhen: (item) => !item.horaSalida
      },
      {
        key: 'authorize',
        icon: 'Check',
        tooltip: 'Autorizar Horas',
        className: 'text-blue-400 hover:text-blue-600',
        showWhen: (item, props) => {
          const isAdmin = props?.isAdmin;
          return Boolean(isAdmin) && item.horaSalida && !item.isAprobado;
        }
      },
    ],
  },
];

export const workedHoursColumnsExcel = [
  { header: 'Nombre del Empleado', key: 'nombreEmpleado' },
  { header: 'Documento', key: 'cedulaEmpleado' },
  { header: 'Centro de Lavado', key: 'nombreLavanderia' },
  { header: 'Fecha', key: 'fecha' },
  { header: 'Hora de Llegada', key: 'horaIngreso' },
  { header: 'Fecha de Salida', key: 'fechaSalida' },
  { header: 'Hora de Salida', key: 'horaSalida' },
  { header: 'Horas entre llegada y salida', key: 'horasReales' },
  { header: 'Total Horas Trabajadas', key: 'totalHorasTrabajadas' },
  { header: 'Hora Ingreso Planificada', key: 'horaIngresoPlanificada' },
  { header: 'Hora Salida Planificada', key: 'horaSalidaPlanificada' },
  { header: 'Hora Ingreso autorizada' , key: 'horaIngresoAutorizada'},
  { header: 'Hora Salida autorizada' , key: 'horaSalidaAutorizada'},
  { header: 'Total Horas Autorizadas', key: 'totalHorasAutorizadas' },
  { header: 'Estado', key: 'isAprobado' },
  { header: 'Planificación al llegar', key: 'planificacionLlegada' },
];

export const workedHoursDataMapper = (item) => {
  const info = salidaInfo(item);
  return {
    nombreEmpleado: item.empleado?.nombre,
    cedulaEmpleado: item.empleado?.cedula,
    fecha: item.fecha ? fechaBogota(item.fecha) : '',
    horaIngreso: item.horaIngreso,
    fechaSalida: info ? fechaBogota(info.salida) : '',
    horaSalida: item.horaSalida,
    horasReales: info ? Number(info.horasReales.toFixed(2)) : '',
    totalHorasTrabajadas: item.totalHorasTrabajadas,
    totalHorasAutorizadas: item.totalHorasAutorizadas,
    horaIngresoPlanificada: item.horaIngresoPlanificada,
    horaSalidaPlanificada: item.horaSalidaPlanificada,
    horaIngresoAutorizada: item.horaIngresoAutorizada,
    horaSalidaAutorizada: item.horaSalidaAutorizada,
    isAprobado: item.isAprobado ? 'Aprobado' : 'Pendiente',
    nombreLavanderia: item.lavanderia?.nombre,
    planificacionLlegada: !item.planificacion
      ? ''
      : item.planificacion.planificado
        ? `Planificado ${item.planificacion.turno || ''}`.trim()
        : item.planificacion.excepcion
          ? `Excepción: ${item.planificacion.excepcion.motivo}`
          : 'Sin planificar',
  };
};
